/**
 * Classifying a link destination for a human — the pure half of the
 * confirm-before-navigate gate.
 *
 * Kept OUT of `react/` deliberately. These are decisions about a URL string,
 * not about rendering, and burying them behind a `"use client"` module that
 * imports react means they cannot be unit-tested in this repo (which installs
 * no react) and cannot be reused server-side. That is the same mistake, one
 * scale down, as the interstitial living inside the email renderer.
 */

/** A classified external destination, ready to show a human. */
export interface ExternalLinkTarget {
  /** The raw href as authored, opened verbatim on confirm. */
  href: string;
  /** Human-readable host, or null when it cannot be parsed. */
  host: string | null;
}

/**
 * Classify an anchor href.
 *
 * Returns a target for http/https links — the ones that need a confirmation —
 * and null for everything else. Null is the safe answer: a caller that cannot
 * classify a link must not navigate to it. In particular `javascript:` and
 * `data:` land here, and a caller that treats null as "open it anyway" has
 * reintroduced the bug this exists to prevent.
 */
export function classifyExternalHref(rawHref: string | null): ExternalLinkTarget | null {
  if (!rawHref) return null;
  const href = rawHref.trim();
  try {
    const url = new URL(href);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return { href, host: url.host || null };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Is this a protocol handler that should open directly, with no interstitial?
 *
 * `mailto:`/`tel:` hand off to the OS and reveal nothing about a remote server,
 * so an "are you sure" on them is friction with no security value. Everything
 * else that is not http(s) — relative, fragments, `javascript:` — is a no-op
 * for the caller, which is why this answers a narrow yes/no rather than
 * returning a URL somebody might then navigate to.
 */
export function isDirectOpenProtocol(rawHref: string | null | undefined): boolean {
  return /^(mailto|tel):/i.test((rawHref ?? "").trim());
}
